/**
 * FoodGapsWidget — Admin curation queue (iter723).
 *
 * Lists the top most-searched food queries that DID NOT resolve to Nutrami
 * (i.e. they resolved to USDA or returned nothing). Each row shows:
 *   • query text + hit_count
 *   • the USDA record we currently match against (name + fdc_id + kcal)
 *   • "Promote to Nutrami" button → opens the existing AddFromUSDAModal
 *     pre-populated with the matched USDA row.
 *   • "Dismiss" button — mark this gap as not-a-curation-candidate.
 *
 * Backend contract:
 *   GET   /api/admin/food-gaps/top?status=open&limit=30
 *   PATCH /api/admin/food-gaps/{gap_id}  body={status: "promoted"|"dismissed"}
 *
 * When the admin successfully imports via AddFromUSDAModal the gap's status
 * is bumped to "promoted" via `onPromoteComplete` callback.
 */
import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent } from "../ui/card";
import { Button } from "../ui/button";
import { TrendingUp, ArrowRight, X, Loader2, RefreshCw, ChevronDown, ChevronUp } from "lucide-react";

export default function FoodGapsWidget({ API, bg, onPromote }) {
  const [expanded, setExpanded] = useState(true);
  const [loading, setLoading]   = useState(false);
  const [gaps, setGaps]         = useState([]);
  const [total, setTotal]       = useState(0);
  const [promotingId, setPromotingId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/food-gaps/top`, {
        params: { limit: 30, status: "open", min_hits: 1 },
      });
      setGaps(r.data?.gaps || []);
      setTotal(r.data?.total || 0);
    } catch (err) {
      toast.error(err?.response?.data?.detail || (bg ? "Грешка при зареждане" : "Load failed"));
    }
    setLoading(false);
  }, [API, bg]);

  useEffect(() => { load(); }, [load]);

  const dismiss = async (gap) => {
    if (!window.confirm(bg
      ? `Скрий "${gap.query_display}" от опашката?`
      : `Dismiss "${gap.query_display}" from queue?`)) return;
    try {
      await axios.patch(`${API}/admin/food-gaps/${gap.id}`, { status: "dismissed" });
      setGaps(prev => prev.filter(g => g.id !== gap.id));
      toast.success(bg ? "Скрито" : "Dismissed");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error");
    }
  };

  const promote = async (gap) => {
    if (!gap.matched_fdc_id) {
      toast.error(bg
        ? "Няма съответствие в USDA — добави ръчно"
        : "No USDA match — add manually");
      return;
    }
    setPromotingId(gap.id);
    try {
      // Fetch the full USDA row so AddFromUSDAModal has kcal/macros/category
      const r = await axios.get(`${API}/admin/nutrami/usda-browse`, {
        params: { q: gap.matched_usda_name || gap.query_display, limit: 5, only_new: false },
      });
      const match = (r.data?.items || []).find(x => x.fdc_id === gap.matched_fdc_id)
                   || (r.data?.items || [])[0];
      if (!match) {
        toast.error(bg ? "USDA запис не намерен" : "USDA record not found");
        setPromotingId(null);
        return;
      }
      // Hand off to NutramiManager's Add modal + preload BG name hint.
      onPromote?.({
        ...match,
        _prefill_name_bg: gap.query_display,
        _gap_id: gap.id,
      });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error");
    }
    setPromotingId(null);
  };

  const layerBadge = (layer) => {
    if (layer === "usda") return "USDA";
    if (layer === "none") return bg ? "Няма" : "None";
    return layer;
  };

  return (
    <Card
      className="border-dashed border-amber-400 dark:border-amber-700 bg-amber-50/50 dark:bg-amber-950/20"
      data-testid="food-gaps-widget"
    >
      <CardContent className="p-4 space-y-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <TrendingUp className="w-4 h-4 text-amber-600" />
            <div>
              <h4 className="text-sm font-bold">
                {bg ? "Опашка за куратиране" : "Curation queue"}
              </h4>
              <p className="text-[10px] text-amber-800 dark:text-amber-300">
                {bg
                  ? `Топ ${gaps.length} най-търсени храни които не са в Nutrami · общо ${total} записа`
                  : `Top ${gaps.length} most-searched foods not in Nutrami · ${total} total`}
              </p>
            </div>
          </div>
          <div className="flex gap-1">
            <Button
              variant="ghost" size="sm"
              onClick={load} disabled={loading}
              className="h-7 w-7 p-0"
              title={bg ? "Обнови" : "Refresh"}
              data-testid="food-gaps-refresh"
            >
              {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
            </Button>
            <Button
              variant="ghost" size="sm"
              onClick={() => setExpanded(v => !v)}
              className="h-7 w-7 p-0"
              data-testid="food-gaps-toggle"
            >
              {expanded ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
            </Button>
          </div>
        </div>

        {expanded && (
          gaps.length === 0 ? (
            <p className="text-xs text-muted-foreground italic py-4 text-center">
              {loading
                ? (bg ? "Зареждане…" : "Loading…")
                : (bg
                    ? "🎉 Няма чакащи! Всички търсения се резолват в Nutrami."
                    : "🎉 No pending gaps! All searches resolve to Nutrami.")}
            </p>
          ) : (
            <div className="space-y-1.5 max-h-80 overflow-y-auto">
              {gaps.map(g => (
                <div
                  key={g.id}
                  className="flex items-center gap-2 p-2 rounded bg-white dark:bg-gray-900 border border-amber-200 dark:border-amber-800/40"
                  data-testid={`food-gap-row-${g.id}`}
                >
                  <div className="flex-shrink-0 min-w-[38px] text-center">
                    <div className="text-sm font-bold text-amber-700">{g.hit_count}</div>
                    <div className="text-[8px] text-muted-foreground">hits</div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-semibold truncate">{g.query_display}</div>
                    <div className="text-[10px] text-muted-foreground truncate">
                      {g.matched_usda_name ? (
                        <>
                          <span className="text-blue-600 font-medium">USDA:</span> {g.matched_usda_name}
                          {g.matched_usda_kcal != null && <span> · {g.matched_usda_kcal}kcal</span>}
                          {g.matched_fdc_id && <span> · fdc={g.matched_fdc_id}</span>}
                        </>
                      ) : (
                        <span className="italic">{bg ? "няма USDA съответствие" : "no USDA match"}</span>
                      )}
                    </div>
                  </div>
                  <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded ${
                    g.top_layer === "usda"
                      ? "bg-blue-100 text-blue-800 dark:bg-blue-950 dark:text-blue-300"
                      : "bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-400"
                  }`}>
                    {layerBadge(g.top_layer)}
                  </span>
                  <Button
                    size="sm"
                    onClick={() => promote(g)}
                    disabled={promotingId === g.id || !g.matched_fdc_id}
                    className="h-7 px-2 text-[10px] gradient-primary text-white flex-shrink-0"
                    data-testid={`food-gap-promote-${g.id}`}
                    title={bg ? "Добави в Nutrami" : "Add to Nutrami"}
                  >
                    {promotingId === g.id
                      ? <Loader2 className="w-3 h-3 animate-spin" />
                      : <><ArrowRight className="w-3 h-3 mr-0.5" />{bg ? "Куратирай" : "Curate"}</>}
                  </Button>
                  <Button
                    size="sm" variant="ghost"
                    onClick={() => dismiss(g)}
                    className="h-7 w-7 p-0 text-gray-400 hover:text-red-500"
                    data-testid={`food-gap-dismiss-${g.id}`}
                    title={bg ? "Скрий" : "Dismiss"}
                  >
                    <X className="w-3 h-3" />
                  </Button>
                </div>
              ))}
            </div>
          )
        )}
      </CardContent>
    </Card>
  );
}
